import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Wallet, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PaymentSuccessPage() {
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  
  const sessionId = searchParams.get('session_id');

  const checkPaymentStatus = useCallback(async (sid) => {
    try {
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 2000;

      const poll = async () => {
        const response = await paymentsAPI.status(sid);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          toast.success('Payment successful!');
          return;
        }
        
        if (response.data.status === 'expired' || attempts >= maxAttempts) {
          setStatus('failed');
          toast.error('Payment failed or expired');
          return;
        }
        
        attempts++;
        setTimeout(poll, pollInterval);
      };

      await poll();
    } catch (err) {
      console.error('Payment status check failed:', err);
      setStatus('failed');
    }
  }, [refreshUser]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('failed');
    }
  }, [sessionId, checkPaymentStatus]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white/70 backdrop-blur-xl border-b border-[#E5E5E5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#22C55E] rounded-sm flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" weight="bold" />
            </div>
            <span className="text-xl font-bold tracking-tight text-[#111111]" style={{ fontFamily: 'Cabinet Grotesk' }}>Divispend</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border border-[#E5E5E5] rounded-sm shadow-none text-center" data-testid="payment-result-card">
          <CardContent className="p-8">
            {status === 'checking' && (
              <>
                <div className="animate-spin w-12 h-12 border-4 border-[#22C55E] border-t-transparent rounded-full mx-auto mb-6"></div>
                <h2 className="text-xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  Processing Payment
                </h2>
                <p className="text-[#555555]">
                  Please wait while we confirm your payment...
                </p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-[#00875A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#00875A]" weight="fill" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  Payment Successful!
                </h2>
                <p className="text-[#555555] mb-6">
                  Your purchase has been completed successfully.
                </p>
                <div className="flex gap-3">
                  <Link to="/explore" className="flex-1">
                    <Button variant="outline" className="w-full rounded-sm border-[#E5E5E5] hover:bg-[#F5F5F7]" data-testid="continue-shopping-button">
                      Continue Shopping
                    </Button>
                  </Link>
                  <Link to="/dashboard" className="flex-1">
                    <Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="go-to-dashboard-button">
                      Dashboard
                    </Button>
                  </Link>
                </div>
              </>
            )}
            
            {status === 'failed' && (
              <>
                <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-[#FF3B30]" weight="fill" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  Payment Failed
                </h2>
                <p className="text-[#555555] mb-6">
                  Something went wrong with your payment. Please try again.
                </p>
                <div className="flex gap-3">
                  <Link to="/explore" className="flex-1">
                    <Button variant="outline" className="w-full rounded-sm border-[#E5E5E5] hover:bg-[#F5F5F7]" data-testid="try-again-button">
                      Try Again
                    </Button>
                  </Link>
                  <Link to="/dashboard" className="flex-1">
                    <Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="go-to-dashboard-button">
                      Dashboard
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
